import {
  Alert,
  Checkbox,
  CircularProgress,
  FormControlLabel,
  List,
  ListItem,
  Stack,
  Typography,
} from "@mui/material";
import type { HunterTerminalDto } from "Infrastructure/Api/Api";
import { Resources, useResource } from "Translations/Resources";

const RESOURCES = Resources.Components.Merchant.OrbitL.TerminalSelection;

type Props = {
  terminals: Array<HunterTerminalDto>;
  selectedTerminalIDs: Array<string>;
  assignedTerminalIDs?: ReadonlyArray<string>;
  requiredCount: number;
  isLoading: boolean;
  onSelectionChange: (selectedIDs: Array<string>) => void;
};

export const TerminalSelectionPanel = ({
  terminals,
  selectedTerminalIDs,
  assignedTerminalIDs = [],
  requiredCount,
  isLoading,
  onSelectionChange,
}: Props) => {
  const { t } = useResource();

  if (isLoading) {
    return <CircularProgress size={24} />;
  }

  if (terminals.length === 0) {
    return (
      <Alert severity="warning">{t(RESOURCES.NoTerminalsAvailable)}</Alert>
    );
  }

  const handleToggle = (publicID: string) => {
    if (assignedTerminalIDs.includes(publicID)) {
      return;
    }

    if (selectedTerminalIDs.includes(publicID)) {
      onSelectionChange(selectedTerminalIDs.filter((id) => id !== publicID));
      return;
    }

    if (selectedTerminalIDs.length >= requiredCount) {
      return;
    }

    onSelectionChange([...selectedTerminalIDs, publicID]);
  };

  const isSelectionComplete = selectedTerminalIDs.length === requiredCount;

  return (
    <Stack gap={1}>
      <Typography variant="body2" color="text.secondary">
        {t(RESOURCES.SelectionCount, {
          selected: selectedTerminalIDs.length,
          required: requiredCount,
        })}
      </Typography>
      <List disablePadding>
        {terminals.map(({ publicID, serialNumber, hardwareModel }) => {
          const isAssigned = assignedTerminalIDs.includes(publicID);
          const isSelected = selectedTerminalIDs.includes(publicID);
          const isDisabled = isAssigned || (!isSelected && isSelectionComplete);

          return (
            <ListItem key={publicID} disablePadding>
              <FormControlLabel
                control={
                  <Checkbox
                    checked={isSelected}
                    onChange={() => handleToggle(publicID)}
                    disabled={isDisabled}
                    size="small"
                  />
                }
                label={
                  <Typography variant="body2">
                    {t(RESOURCES.SerialNumber)}: {serialNumber}
                    {hardwareModel &&
                      ` — ${t(RESOURCES.HardwareModel)}: ${hardwareModel}`}
                  </Typography>
                }
              />
            </ListItem>
          );
        })}
      </List>
    </Stack>
  );
};
